import useDashboard from "../hooks/useDashboard";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import API from "../api/api";
import {
  PieChart, Pie, Cell,
  BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";

const activeReservationStatuses = ["PENDING", "APPROVED", "CHECKED_OUT"];

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const startOfLocalDay = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const rangesOverlap = (startA, endA, startB, endB) => startA <= endB && endA >= startB;

const formatDate = (value) => new Date(value).toLocaleDateString("nl-BE", {
  day: "2-digit",
  month: "2-digit"
});

const formatCurrency = (value) => new Intl.NumberFormat("nl-BE", {
  style: "currency",
  currency: "EUR"
}).format(Number(value || 0));

function KpiCard({ label, value, description }) {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {description && <p className="page-subtitle">{description}</p>}
    </div>
  );
}

function FleetCalendar({ trailers, reservations }) {
  const [monthDate, setMonthDate] = useState(new Date());
  const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  const offset = (firstDay.getDay() + 6) % 7;
  const cells = [];
  const visibleReservations = useMemo(() => reservations.filter((reservation) => (
    activeReservationStatuses.includes(reservation.status)
  )), [reservations]);

  for (let i = 0; i < offset; i += 1) cells.push({ key: `empty-${i}`, empty: true });

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    const dayStart = startOfLocalDay(date);
    const busyReservations = visibleReservations.filter((reservation) => (
      rangesOverlap(
        dayStart,
        dayStart,
        startOfLocalDay(reservation.startDate),
        startOfLocalDay(reservation.endDate)
      )
    ));
    const busyTrailerIds = new Set(busyReservations.map((reservation) => reservation.trailerId));
    const availableCount = Math.max(trailers.length - busyTrailerIds.size, 0);

    cells.push({
      key: toDateInput(date),
      day,
      busyReservations,
      busyTrailerIds,
      availableCount
    });
  }

  const monthLabel = monthDate.toLocaleDateString("nl-BE", { month: "long", year: "numeric" });

  return (
    <div className="panel panel-pad dashboard-calendar">
      <div className="calendar-header">
        <button className="button secondary" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}>Vorige</button>
        <div>
          <h3 className="chart-title">Trailerplanning</h3>
          <p className="page-subtitle">{monthLabel}</p>
        </div>
        <button className="button secondary" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}>Volgende</button>
      </div>

      <div className="fleet-calendar-grid">
        {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((day) => (
          <div key={day} className="calendar-weekday">{day}</div>
        ))}
        {cells.map((cell) => (
          <div key={cell.key} className={`fleet-calendar-day${cell.empty ? " empty" : ""}${cell.busyReservations?.length ? " has-bookings" : ""}`}>
            {!cell.empty && (
              <>
                <div className="fleet-day-top">
                  <strong>{cell.day}</strong>
                  <span>{cell.availableCount}/{trailers.length} vrij</span>
                </div>
                <div className="fleet-day-bookings">
                  {cell.busyReservations.slice(0, 3).map((reservation) => (
                    <div key={reservation.id} className="fleet-booking">
                      {reservation.trailer?.name || `Trailer #${reservation.trailerId}`}
                    </div>
                  ))}
                  {cell.busyReservations.length > 3 && (
                    <div className="fleet-booking more">+{cell.busyReservations.length - 3} extra</div>
                  )}
                  {cell.busyReservations.length === 0 && (
                    <div className="fleet-available">Alle trailers beschikbaar</div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="fleet-summary-list">
        {visibleReservations.slice(0, 6).map((reservation) => (
          <div key={reservation.id} className="fleet-summary-row">
            <strong>{reservation.trailer?.name || `Trailer #${reservation.trailerId}`}</strong>
            <span>{formatDate(reservation.startDate)} tot {formatDate(reservation.endDate)}</span>
            <span>{reservation.name}</span>
          </div>
        ))}
        {visibleReservations.length === 0 && (
          <div className="empty-state">Geen actieve reservaties in de planning.</div>
        )}
      </div>
    </div>
  );
}

function DashboardAlerts({ alerts }) {
  const items = [
    {
      key: "newReservations",
      label: "Nieuwe reservaties",
      value: alerts.newReservations,
      tone: "info",
      description: "Aanvragen die nog wachten op goedkeuring."
    },
    {
      key: "openDamageCases",
      label: "Openstaande schadegevallen",
      value: alerts.openDamageCases,
      tone: "danger",
      description: "Schadegevallen die nog niet hersteld of gesloten zijn."
    },
    {
      key: "pendingDeposits",
      label: "Waarborgen nog niet terugbetaald",
      value: alerts.pendingDeposits,
      tone: "warning",
      description: "Afgeronde reservaties met openstaande waarborg."
    },
    {
      key: "pickupsToday",
      label: "Vandaag op te halen",
      value: alerts.pickupsToday,
      tone: "success",
      description: "Goedgekeurde of nieuwe reservaties die vandaag starten."
    }
  ];

  return (
    <div className="dashboard-alert-grid">
      {items.map((item) => (
        <div key={item.key} className={`dashboard-alert-card ${item.tone}${item.value === 0 ? " quiet" : ""}`}>
          <div className="dashboard-alert-label">{item.label}</div>
          <div className="dashboard-alert-value">{item.value}</div>
          <p>{item.description}</p>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { data, isLoading, isError } = useDashboard();
  const { data: reservations = [], isLoading: reservationsLoading, isError: reservationsError } = useQuery({
    queryKey: ["reservations"],
    queryFn: async () => (await API.get("/reservations")).data
  });
  const { data: trailers = [], isLoading: trailersLoading, isError: trailersError } = useQuery({
    queryKey: ["trailers"],
    queryFn: async () => (await API.get("/trailers")).data
  });

  if (isLoading) {
    return <div className="loading-state">Dashboard laden...</div>;
  }

  if (isError) {
    return <div className="error-state">Dashboard laden mislukt</div>;
  }

  const { kpis, topTrailers } = data;
  const alerts = {
    newReservations: 0,
    openDamageCases: 0,
    pendingDeposits: 0,
    pickupsToday: 0,
    ...(data.alerts || {})
  };

  const pieData = [
    { name: "Omzet", value: kpis.totalRevenue },
    { name: "Schade", value: kpis.totalDamage },
    { name: "Terugbetaald", value: kpis.totalRefunded }
  ];

  const COLORS = ["#16a34a", "#dc2626", "#2563eb"];

  const barData = topTrailers.map(t => ({
    name: `T${t.trailerId}`,
    revenue: t.revenue
  }));

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Omzet, schade en reservatieactiviteit in een overzicht.</p>
        </div>
      </header>

      <DashboardAlerts alerts={alerts} />

      <div className="kpi-grid">
        <KpiCard
          label="Actieve reservaties"
          value={kpis.activeReservations ?? 0}
          description="Lopende of toekomstige reservaties."
        />
        <KpiCard
          label="Omzet deze maand"
          value={kpis.monthlyRevenueAvailable === false ? "Niet beschikbaar" : formatCurrency(kpis.monthlyRevenue)}
          description="Berekend op basis van reservatieperiode en dagprijs."
        />
        <KpiCard
          label="Open waarborgen"
          value={formatCurrency(kpis.openDepositAmount)}
          description={`${kpis.openDeposits ?? 0} waarborg(en) nog open.`}
        />
        <KpiCard
          label="Beschikbare trailers"
          value={kpis.availableTrailers ?? 0}
          description="Trailers met status beschikbaar."
        />
        <KpiCard
          label="Verhuurde trailers"
          value={kpis.rentedTrailers ?? 0}
          description="Trailers met status verhuurd of in gebruik."
        />
        <KpiCard
          label="Reservaties deze maand"
          value={kpis.reservationsThisMonth ?? 0}
          description="Op basis van startdatum."
        />
        <KpiCard
          label="Schadegevallen open"
          value={kpis.openDamageCases ?? alerts.openDamageCases}
          description="Nog niet hersteld of gesloten."
        />
      </div>

      {reservationsError || trailersError ? (
        <div className="error-state panel panel-pad">Kalender laden mislukt</div>
      ) : reservationsLoading || trailersLoading ? (
        <div className="loading-state panel panel-pad">Kalender laden...</div>
      ) : (
        <FleetCalendar trailers={trailers} reservations={reservations} />
      )}

      <div className="chart-grid">
        <div className="panel panel-pad">
          <h3 className="chart-title">Verdeling omzet</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={pieData} dataKey="value" outerRadius={90} label>
                {pieData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="panel panel-pad">
          <h3 className="chart-title">Omzet per trailer</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={barData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="revenue" fill="#2563eb" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}
